import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { FilterList as FilterIcon, Add as AddIcon } from '@mui/icons-material';

import { useApiGet } from '../../../utils/httpClientTs';
import { getSeekingTransferList } from '../../../endpoints/seekingTransfer';
import {
  SeekingTransferListType,
  PaginatedSeekingTransferResponse,
  SeekingTransferChapterList,
} from '../../../types/seekingTransfer';
import { SortOrder } from '../../../types/table';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import SPDSelector from '../../../Components/SPDSelector';
import UserSelectModal from '../../../Components/UserSearchModal';

// Status constants
const STATUS_OPTIONS = [
  { value: '60baf6c89e6f4343900e4dba8188548d', label: 'Open' },
  { value: '693dbd61eb194b99935c34dfefa0fe91', label: 'Pending' },
  { value: '92f6b54d6bc64442a27beadc1d32e311', label: 'Removed' },
  { value: '2ca52f70ef7c49d9851d6205128aebf0', label: 'Completed' },
];

// Function to render local chapters with visual indicators
const renderLocalChapters = (
  chapters: SeekingTransferChapterList[] | undefined
) => {
  if (!chapters || !Array.isArray(chapters)) {
    return (
      <Typography variant="body2" color="text.secondary">
        No chapters
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {chapters.map((chapter) => (
        <Chip
          key={chapter.id}
          label={chapter.name}
          size="small"
          sx={{
            backgroundColor: chapter.chapter_contact_made
              ? '#aaf0c9'
              : '#57B9FF',
            color: 'text.primary',
            fontWeight: chapter.chapter_contact_made ? 'bold' : 'normal',
            '&:hover': {
              backgroundColor: chapter.chapter_contact_made
                ? '#aaf0c9'
                : '#57B9FF',
            },
          }}
        />
      ))}
    </Box>
  );
};

const columns: ServerSideTableColumn<SeekingTransferListType>[] = [
  {
    id: 'name',
    label: 'Member Name',
    width: '20%',
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    width: '20%',
    sortable: true,
  },
  {
    id: 'created_at',
    label: 'Created At',
    width: '20%',
    sortable: true,
  },
  {
    id: 'local_chapters',
    label: 'Local Chapters',
    width: '40%',
    sortable: false,
    render: (chapters: SeekingTransferChapterList[] | undefined) =>
      renderLocalChapters(chapters),
  },
];

export default function SeekingTransferList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [spdId, setSpdId] = useState<string>(searchParams.get('spd_id') || '');
  const [statusId, setStatusId] = useState<string>(
    searchParams.get('status_id') || ''
  );
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showUserSearchModal, setShowUserSearchModal] =
    useState<boolean>(false);
  const navigate = useNavigate();

  // need to update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // Update search params when filters change
  useEffect(() => {
    updateSearchParams('spd_id', spdId);
    setPage(0); // Reset to first page when filters change
  }, [spdId, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('status_id', statusId);
    setPage(0); // Reset to first page when filters change
  }, [statusId, updateSearchParams]);

  const { data, isFetching, refetch } =
    useApiGet<PaginatedSeekingTransferResponse>(
      [
        'seeking-transfer',
        rowsPerPage,
        page,
        orderBy,
        order,
        search,
        spdId,
        statusId,
      ],
      getSeekingTransferList,
      {
        enabled: true,
        retry: 1,
      },
      {
        rowsPerPage,
        page,
        orderBy,
        order,
        search,
        spd_id: spdId,
        status_id: statusId,
      }
    );

  // Refresh data when component mounts to ensure fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (row: SeekingTransferListType) => {
    window.location.href = `/seeking-transfer/${row.id}`;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleSort = (property: keyof SeekingTransferListType) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(String(property));
    setPage(0); // Reset to first page when sorting
  };

  const handleResetFilters = () => {
    setSpdId('');
    setStatusId('');
    setSearch('');
    setDelaySearch('');
    setPage(0); // Reset to first page when filters change
  };

  const handleAddTransferRequest = () => {
    setShowUserSearchModal(true);
  };

  const handleUserSelected = (user: any) => {
    if (user && user.id) {
      navigate(`/seeking-transfer/create/${user.id}`);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} padding={1}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Members Seeking Transfers</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTransferRequest}
            sx={{ minWidth: 200 }}
          >
            Add Transfer Request
          </Button>
        </Box>
      </Grid>

      {/* Search and Filters */}
      <Grid item xs={12} padding={1}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                id="search-field"
                size="small"
                value={delaySearch}
                onChange={(event) => setDelaySearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" onClick={handleResetFilters}>
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          {showFilters && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SPDSelector
                    spd={spdId}
                    handleSpdSelect={(value) => setSpdId(value || '')}
                    reportId="seeking-transfer-list"
                    all={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusId}
                      onChange={(e) => setStatusId(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} padding={1}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isFetching}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={orderBy}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleClick}
          emptyMessage="No seeking transfer requests found"
        />
      </Grid>

      {/* User Search Modal */}
      <UserSelectModal
        modalOpen={showUserSearchModal}
        setUser={handleUserSelected}
        setModalOpen={setShowUserSearchModal}
        chapter={null}
        spd={null}
        activeMembers={true}
        userObjectRet={true}
        allowSetToMe={true}
      />
    </Grid>
  );
}
